<template>
    <div class="flex flex-row">
        <div class="text-center m-auto pt-8" v-if="!loaded">
            <a-spin size="large" />
        </div>
        <div class="border-r-2 w-56" v-if="loaded && intermediaries_list.length > 0 && display_type == 'page'">
            <a-anchor
                :affix="true"
                wrapperClass="w-56"
                class="cover-anchor"
                @click="handleClick"
                :items="anchorItems"
            />
        </div>
        <div class="form-container" data-cy="form-container" v-if="loaded && intermediaries_list.length <= 0">
            <div class="form-section flex flex-col">
                <div
                    class="text-3xl mb-2 pb-2 font-light flex items-end justify-between ml-4 borderline"
                    data-cy="cover-information-form-header"
                    id="cover-information"
                >
                    <div>
                        {{ $t('covers.forms.title.cover_info') }}
                    </div>
                </div>
            </div>
            <div class="form-container ml-4" data-cy="intermediaries error">
                <div>
                    {{ $t('covers.forms.no_intermediaries.body_1') }}
                    <span class="font-bold">{{ insurer_list[0].text }}. </span>
                    <span>{{ $t('covers.forms.no_intermediaries.body_2') }}</span>
                </div>
            </div>
        </div>
        <form
            @change="isFormDirty"
            @submit.prevent="handleSubmit"
            class="form-container"
            data-cy="form-container"
            v-if="loaded && intermediaries_list.length > 0"
        >
            <p class="text-center text-default-red p-2" v-if="validation.message">
                {{ validation.message }}
            </p>
            <div class="form-section flex flex-col">
                <div v-if="display_type != 'drawer'" class="w-full flex flex-row justify-end pr-2 mb-2">
                    <a-tag v-if="cover_id != ''" color="blue"> Cover id: {{ cover.cover_id }} </a-tag>
                </div>
                <div
                    class="text-3xl mb-2 pb-2 font-light flex items-end justify-between ml-4 borderline"
                    :class="{ 'pt-2': display_type == 'drawer' }"
                    data-cy="cover-information-form-header"
                    id="cover-information"
                >
                    <div>
                        {{ $t('covers.forms.title.cover_info') }}
                    </div>
                    <div class="text-default-blue-green" v-if="!readOnly">
                        {{
                            $t('covers.forms.title.step', {
                                step_num: 1,
                            })
                        }}
                    </div>
                </div>
            </div>
            <div class="form-section flex flex-col" v-if="readOnly">
                <div
                    class="section-body w-full flex flex-row"
                    :class="display_type == 'page' ? 'ml-4 lg:w-10/12 xl:w-3/4 2xl:w-1/2' : 'p-4'"
                    data-cy="section-general"
                >
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="cover-insurer-field">
                        <ui-label>{{ $t('covers.forms.main.insurer') }}</ui-label>
                        <div class="subText">{{ cover.insurer_name }}</div>
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="cover-intermediary-field">
                        <ui-label>{{ $t('covers.forms.main.intermediary') }}</ui-label>
                        <div class="subText">
                            <span v-for="record in intermediaries_list" :key="record.value">
                                <template v-if="record.value === cover.company_id">
                                    {{ record.text }}
                                </template>
                            </span>
                        </div>
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="cover-policy-number-field">
                        <ui-label>{{ $t('covers.forms.main.policy_no') }}</ui-label>
                        <div class="subText">{{ cover.insurer_policy_number }}</div>
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="cover-broker-policy-number-field">
                        <ui-label>{{ $t('covers.forms.main.broker_policy_no') }}</ui-label>
                        <div class="subText">{{ cover.broker_policy_number }}</div>
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="cover-policy-start-date-field">
                        <ui-label>{{ $t('covers.forms.main.policy_start_date') }}</ui-label>
                        <div class="subText">{{ cover.policy_start_date }}</div>
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="cover-policy-end-date-field">
                        <ui-label>{{ $t('covers.forms.main.policy_end_date') }}</ui-label>
                        <div class="subText">{{ cover.policy_end_date }}</div>
                    </div>
                    <div class="input-container checkbox-container mb-2 w-full">
                        <div class="cb-viewonly" data-cy="cover-is-motor-trade-field">
                            <ui-checkbox v-model="cover.is_motor_trade" :disabled="true">
                                {{ $t('covers.forms.main.is_motor_trade') }}
                            </ui-checkbox>
                        </div>
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="cover-effective-date-field">
                        <ui-label>{{ $t('covers.forms.main.cover_effective_date') }}</ui-label>
                        <div class="subText">{{ cover.cover_effective_date }}</div>
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="cover-expiry-date-field">
                        <ui-label>{{ $t('covers.forms.main.cover_expiry_date') }}</ui-label>
                        <div class="subText">{{ cover.cover_expiry_date }}</div>
                    </div>
                    <div class="flex w-full">
                        <div class="flex flex-col w-1/2">
                            <div class="w-full mb-6 mt-4 pr-4" data-cy="cover-status-field">
                                <ui-label>
                                    {{ $t('covers.forms.main.cover_status') }}
                                    <span
                                        v-if="v$.cover.cover_expiry_date.$model != null"
                                        class="ml-1"
                                        :class="{
                                            'text-default-green':
                                                (cover.cover_status == null || cover.cover_status == 'active') &&
                                                dayjs(v$.cover.cover_expiry_date.$model).diff(dayjs()) > 0,
                                            'text-default-gray':
                                                (cover.cover_status == null || cover.cover_status == 'active') &&
                                                dayjs(v$.cover.cover_expiry_date.$model).diff(dayjs()) <= 0,
                                            'text-default-red': cover.cover_status == 'cancel',
                                        }"
                                    >
                                        {{
                                            (cover.cover_status == null || cover.cover_status == 'active') &&
                                            dayjs(v$.cover.cover_expiry_date.$model).diff(dayjs()) > 0
                                                ? $t('covers.status.active').toUpperCase()
                                                : (cover.cover_status == null || cover.cover_status == 'active') &&
                                                    dayjs(v$.cover.cover_expiry_date.$model).diff(dayjs()) <= 0
                                                  ? $t('covers.status.expired').toUpperCase()
                                                  : cover.cover_status == 'cancel'
                                                    ? $t('covers.status.cancelled').toUpperCase()
                                                    : ''
                                        }}
                                    </span>
                                </ui-label>
                            </div>
                            <div class="input-container mb-2 pr-4" data-cy="cover-days-of-cover">
                                <ui-label>{{ $t('covers.forms.main.days_of_cover') }}</ui-label>
                                <div class="w-1/2">{{ days }}</div>
                            </div>
                            <div class="input-container mb-2 pr-4" data-cy="cover-certificate-type-field">
                                <ui-label>{{ $t('covers.forms.main.certificate_type') }}</ui-label>
                                <div class="w-1/2">{{ cover.certificate_type }}</div>
                            </div>
                        </div>
                        <div class="flex flex-col w-1/2">
                            <div class="input-container mb-2 mt-4 pr-4" data-cy="cover-address-field">
                                <ui-label>{{ $t('covers.forms.main.address') }}</ui-label>
                                <div class="w-1/2">{{ cover.policyholder_address }}</div>
                            </div>
                        </div>
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="cover-cover-type-field">
                        <ui-label>{{ $t('covers.forms.main.cover_type') }}</ui-label>
                        <div class="subText">{{ cover.cover_type }}</div>
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-8" data-cy="cover-cover-category-field">
                        <ui-label>{{ $t('covers.forms.main.cover_category') }}</ui-label>
                        <div class="w-1/2">{{ cover.cover_category }}</div>
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-8" data-cy="cover-risk-mortgagee-field">
                        <ui-label>{{ $t('covers.forms.main.risk_mortgagee') }}</ui-label>
                        <div class="w-1/2">{{ cover.risk_mortgagee }}</div>
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="cover-manual-cert-field">
                        <ui-label>{{ $t('covers.forms.main.manual_cert_no') }}</ui-label>
                        <div class="subText">{{ cover.manual_certificate_number }}</div>
                    </div>
                    <div class="input-container mb-2 pr-8" data-cy="cover-section-one-field">
                        <ui-label>{{ sectionOneLabel }}</ui-label>
                        <div class="w-1/2">{{ cover.section_1 }}</div>
                    </div>
                    <div class="input-container mb-2 pr-8" data-cy="cover-section-two-field">
                        <ui-label>{{ sectionTwoLabel }}</ui-label>
                        <div class="w-1/2">{{ cover.section_2 }}</div>
                    </div>
                    <div class="input-container mb-2 pr-8" data-cy="cover-section-three-field">
                        <ui-label>{{ sectionThreeLabel }}</ui-label>
                        <div class="w-1/2">{{ cover.section_3 }}</div>
                    </div>
                    <div class="input-container mb-2 pr-8" data-cy="cover-section-four-field">
                        <ui-label>{{ sectionFourLabel }}</ui-label>
                        <div class="w-1/2">{{ cover.section_4 }}</div>
                    </div>
                </div>
                <div class="form-section flex flex-col">
                    <div
                        class="text-3xl mb-2 pb-2 font-light flex items-end justify-between ml-4 borderline"
                        data-cy="vehicle-information-form-header"
                        id="vehicle-information"
                    >
                        <div>
                            {{ $t('covers.forms.title.vehicle_info') }}
                        </div>
                    </div>
                </div>
                <div class="form-section flex flex-col">
                    <div
                        class="section-body w-full flex flex-row"
                        :class="display_type == 'page' ? 'ml-4 lg:w-10/12 xl:w-3/4 2xl:w-1/2' : 'p-4'"
                    >
                        <div
                            class="input-container mb-2 w-1/2 pr-4"
                            data-cy="vehicle-section-registration-number-field"
                        >
                            <ui-label>{{ $t('covers.forms.main.registration_number') }}</ui-label>
                            <div class="subText">{{ cover.registration_number }}</div>
                        </div>
                        <div class="input-container mb-2 w-1/2 pr-4" data-cy="vehicle-section-chassis-number-field">
                            <ui-label>{{ $t('covers.forms.main.chassis_number') }}</ui-label>
                            <div class="subText">{{ cover.chassis_number }}</div>
                        </div>
                        <div class="input-container mb-2 w-1/2 pr-4" data-cy="vehicle-section-vehicle-make-field">
                            <ui-label>{{ $t('covers.forms.main.vehicle_make') }}</ui-label>
                            <div class="subText">{{ cover.vehicle_make }}</div>
                        </div>
                        <div class="input-container mb-2 w-1/2 pr-4" data-cy="vehicle-section-vehicle-model-field">
                            <ui-label>{{ $t('covers.forms.main.vehicle_model') }}</ui-label>
                            <div class="subText">{{ cover.vehicle_model }}</div>
                        </div>
                        <div class="input-container mb-2 w-1/2 pr-4" data-cy="vehicle-section-vehicle-year-field">
                            <ui-label>{{ $t('covers.forms.main.vehicle_year') }}</ui-label>
                            <div class="subText">{{ cover.vehicle_year }}</div>
                        </div>
                        <div class="input-container mb-2 w-1/2 pr-4" data-cy="vehicle-section-vehicle-color-field">
                            <ui-label>{{ $t('covers.forms.main.vehicle_color') }}</ui-label>
                            <div class="subText">{{ cover.vehicle_color }}</div>
                        </div>
                        <div class="input-container mb-2 w-1/2 pr-4" data-cy="vehicle-section-engine-number-field">
                            <ui-label>{{ $t('covers.forms.main.engine_number') }}</ui-label>
                            <div class="subText">{{ cover.engine_number }}</div>
                        </div>
                        <div class="input-container mb-2 w-1/2 pr-4" data-cy="vehicle-section-sum-insured-field">
                            <ui-label>{{ $t('covers.forms.main.sum_insured') }}</ui-label>
                            <div class="subText">{{ formatters.currencyFormatter(cover.sum_insured) }}</div>
                        </div>
                        <div class="input-container mb-2 w-1/2 pr-4" data-cy="vehicle-section-hpcc-field">
                            <ui-label>{{ $t('covers.forms.main.hpcc') }}</ui-label>
                            <div class="subText">{{ cover.hpcc }}</div>
                        </div>
                        <div class="input-container mb-2 w-1/2 pr-4" data-cy="vehicle-section-seating-field">
                            <ui-label>{{ $t('covers.forms.main.seating') }}</ui-label>
                            <div class="subText">{{ cover.seating }}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="form-section flex flex-col" v-if="!readOnly">
                <div
                    class="section-body w-full flex flex-row"
                    :class="display_type == 'page' ? 'ml-4 lg:w-10/12 xl:w-3/4 2xl:w-1/2' : 'p-4'"
                    data-cy="section-general"
                >
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="cover-insurer-field">
                        <ui-label>{{ $t('covers.forms.main.insurer') }}</ui-label>
                        <ui-select
                            v-model="v$.cover.insurer_id.$model"
                            :class="{ 'form-error': v$.cover.insurer_id.$error }"
                            :options="insurer_list"
                            @updatedValue="updateRisks"
                            :isDisabled="form_type == 'insurer' || (form_type == 'intermediary' && cover_id !== '')"
                        />
                        <div
                            data-cy="field-insurer-error"
                            :class="{ 'text-default-red': v$.cover.insurer_id.$error }"
                            v-if="v$.cover.insurer_id.$error"
                        >
                            {{ $t('validation.required') }}
                        </div>
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="cover-intermediary-field">
                        <ui-label>{{ $t('covers.forms.main.intermediary') }}</ui-label>
                        <ui-select
                            v-model="v$.cover.company_id.$model"
                            :class="{ 'form-error': v$.cover.company_id.$error }"
                            :options="intermediaries_list"
                            :isDisabled="form_type == 'intermediary'"
                        />
                        <div
                            data-cy="field-intermediary-error"
                            :class="{ 'text-default-red': v$.cover.company_id.$error }"
                            v-if="v$.cover.company_id.$error"
                        >
                            {{ $t('validation.required') }}
                        </div>
                    </div>
                    <div class="input-container mb-2 pr-4 w-1/2" data-cy="cover-policy-number-field">
                        <ui-label>{{ $t('covers.forms.main.policy_no') }}</ui-label>
                        <ui-input
                            v-model="v$.cover.insurer_policy_number.$model"
                            :class="{ 'form-error': v$.cover.insurer_policy_number.$error }"
                        />
                        <div
                            data-cy="field-policy-number-error"
                            :class="{ 'text-default-red': v$.cover.insurer_policy_number.$error }"
                            v-if="
                                v$.cover.insurer_policy_number.requiredIf.$invalid &&
                                v$.cover.insurer_policy_number.$error
                            "
                        >
                            {{ $t('validation.required') }}
                        </div>
                    </div>
                    <div class="input-container mb-2 pr-4 w-1/2" data-cy="cover-broker-policy-number-field">
                        <ui-label>{{ $t('covers.forms.main.broker_policy_no') }}</ui-label>
                        <ui-input
                            v-model="v$.cover.broker_policy_number.$model"
                            :class="{ 'form-error': v$.cover.broker_policy_number.$error }"
                        />
                    </div>
                    <div class="input-container mb-2 pr-4" data-cy="cover-expiry-date-field">
                        <div class="flex">
                            <ui-label class="w-1/2 mr-2">{{ $t('covers.forms.main.policy_start_date') }}</ui-label>
                            <ui-label class="w-1/2">{{ $t('covers.forms.main.policy_end_date') }}</ui-label>
                        </div>

                        <ui-date-range-picker-double
                            :section="'cover'"
                            :format="'YYYY-MM-DD HH:mm'"
                            :show_time="{ format: 'HH:mm' }"
                            v-model="v$.cover.policy_date_range.$model"
                            ref="policyDateDouble"
                            :class="{
                                'form-error': v$.cover.policy_start_date.$error || v$.cover.policy_end_date.$error,
                            }"
                            @changeDates="updateDatePolicy"
                        />
                        <div
                            data-cy="field-cover-expiry-date-error"
                            :class="{
                                'text-default-red':
                                    v$.cover.policy_start_date.$error || v$.cover.policy_end_date.$error,
                            }"
                            v-if="v$.cover.policy_start_date.$error || v$.cover.policy_end_date.$error"
                        >
                            {{ $t('validation.required') }}
                        </div>
                    </div>
                    <div class="input-container mb-2 mt-4 pr-4 lg:w-1/2 w-2/3">
                        <ui-label>
                            {{ $t('covers.forms.main.period_in_months') }}
                            <a-button data-cy="add-one-month-btn" class="rounded ant-btn-primary" @click="addMonths(1)">
                                1
                            </a-button>
                            <a-button
                                data-cy="add-three-months-btn"
                                class="rounded ant-btn-primary"
                                @click="addMonths(3)"
                            >
                                3
                            </a-button>
                            <a-button
                                data-cy="add-six-months-btn"
                                class="rounded ant-btn-primary"
                                @click="addMonths(6)"
                            >
                                6
                            </a-button>
                            <a-button
                                data-cy="add-twelve-months-btn"
                                class="rounded ant-btn-primary"
                                @click="addMonths(12)"
                            >
                                12
                            </a-button>
                        </ui-label>
                    </div>
                    <div class="input-container checkbox-container mb-2 mt-5 lg:w-1/2 w-1/3">
                        <div data-cy="field-cover-is-motor-trade">
                            <ui-checkbox v-model="cover.is_motor_trade">
                                {{ $t('covers.forms.main.is_motor_trade') }}
                            </ui-checkbox>
                        </div>
                    </div>
                    <div class="input-container mb-2 pr-4" data-cy="cover-date-field">
                        <div class="flex">
                            <ui-label class="w-1/2 mr-2">{{ $t('covers.forms.main.cover_effective_date') }}</ui-label>
                            <ui-label class="w-1/2">{{ $t('covers.forms.main.cover_expiry_date') }}</ui-label>
                        </div>

                        <ui-date-range-picker-double
                            :section="'cover'"
                            :format="'YYYY-MM-DD HH:mm'"
                            :show_time="{ format: 'HH:mm' }"
                            :date_limits="cover_date_limits"
                            v-model="v$.cover.cover_date_range.$model"
                            ref="coverDateDouble"
                            :class="{
                                'form-error': v$.cover.cover_effective_date.$error || v$.cover.cover_expiry_date.$error,
                            }"
                            @changeDates="dateDifference"
                        />
                        <div
                            data-cy="field-cover-date-error"
                            :class="{
                                'text-default-red':
                                    v$.cover.cover_effective_date.$error || v$.cover.cover_expiry_date.$error,
                            }"
                            v-if="v$.cover.cover_effective_date.$error || v$.cover.cover_expiry_date.$error"
                        >
                            {{ $t('validation.required') }}
                        </div>
                    </div>
                    <div class="flex w-full">
                        <div class="flex flex-col w-1/2">
                            <div
                                v-if="section_type != 'renew'"
                                class="w-full mb-6 mt-4 pr-2"
                                data-cy="cover-status-field"
                            >
                                <ui-label>
                                    {{ $t('covers.forms.main.cover_status') }}
                                    <span
                                        v-if="v$.cover.cover_expiry_date.$model != null"
                                        class="ml-1"
                                        :class="{
                                            'text-default-green':
                                                (cover.cover_status == null || cover.cover_status == 'active') &&
                                                dayjs(v$.cover.cover_expiry_date.$model).diff(dayjs()) > 0,
                                            'text-default-gray':
                                                (cover.cover_status == null || cover.cover_status == 'active') &&
                                                dayjs(v$.cover.cover_expiry_date.$model).diff(dayjs()) <= 0,
                                            'text-default-red': cover.cover_status == 'cancel',
                                        }"
                                    >
                                        {{
                                            (cover.cover_status == null || cover.cover_status == 'active') &&
                                            dayjs(v$.cover.cover_expiry_date.$model).diff(dayjs()) > 0
                                                ? $t('covers.status.active').toUpperCase()
                                                : (cover.cover_status == null || cover.cover_status == 'active') &&
                                                    dayjs(v$.cover.cover_expiry_date.$model).diff(dayjs()) <= 0
                                                  ? $t('covers.status.expired').toUpperCase()
                                                  : cover.cover_status == 'cancel'
                                                    ? $t('covers.status.cancelled').toUpperCase()
                                                    : ''
                                        }}
                                    </span>
                                </ui-label>
                            </div>
                            <div class="input-container mb-2 pr-4" data-cy="cover-days-of-cover">
                                <ui-label>{{ $t('covers.forms.main.days_of_cover') }}</ui-label>
                                <ui-input :value="days" :disabled="true"></ui-input>
                            </div>
                            <div class="input-container mb-2 pr-4" data-cy="cover-certificate-type-field">
                                <ui-label>
                                    {{ $t('covers.forms.main.certificate_type') }}
                                    <div
                                        class="text-blue-500 float-right cursor-pointer flex"
                                        @click="showTemplatesModal"
                                        data-cy="field-search-template"
                                    >
                                        {{ $t('covers.forms.main.search_template') }}
                                        <SearchOutlined class="ml-2 self-center" />
                                    </div>
                                </ui-label>
                                <ui-input
                                    v-model="v$.cover.certificate_type.$model"
                                    :class="{ 'form-error': v$.cover.certificate_type.$error }"
                                />
                                <div
                                    data-cy="field-cover-certificate-code-error"
                                    :class="{ 'text-default-red': v$.cover.certificate_type.$error }"
                                    v-if="
                                        v$.cover.certificate_type.required.$invalid && v$.cover.certificate_type.$error
                                    "
                                >
                                    {{ $t('validation.required') }}
                                </div>
                            </div>
                        </div>
                        <div class="flex flex-col w-1/2">
                            <div
                                class="input-container pr-4"
                                :class="{ 'mt-4': section_type != 'renew' }"
                                data-cy="cover-address-field"
                            >
                                <ui-label>{{ $t('covers.forms.main.address') }}</ui-label>
                                <ui-text-area
                                    v-model="v$.cover.policyholder_address.$model"
                                    :rows="defaultRow"
                                    :min="defaultRow"
                                    :max="8"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="cover-cover-type-field">
                        <ui-label>{{ $t('covers.forms.main.cover_type') }}</ui-label>
                        <ui-select
                            v-model="v$.cover.cover_type.$model"
                            :class="{ 'form-error': v$.cover.cover_type.$error }"
                            :options="cover_types"
                            @updatedValue="restrictDate"
                        />
                        <div
                            data-cy="field-cover-cover-type-error"
                            :class="{ 'text-default-red': v$.cover.cover_type.$error }"
                            v-if="v$.cover.cover_type.required.$invalid && v$.cover.cover_type.$error"
                        >
                            {{ $t('validation.required') }}
                        </div>
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="cover-cover-category-field">
                        <ui-label>{{ $t('covers.forms.main.cover_category') }}</ui-label>
                        <ui-select v-model="v$.cover.cover_category.$model" :options="cover_category_list" />
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="cover-risk-mortgagee-field">
                        <ui-label>{{ $t('covers.forms.main.risk_mortgagee') }}</ui-label>
                        <ui-select-lookup v-model="v$.cover.risk_mortgagee.$model" :options="risk_mortgagee_list" />
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="cover-manual-cert-field">
                        <ui-label>{{ $t('covers.forms.main.manual_cert_no') }}</ui-label>
                        <ui-input v-model="v$.cover.manual_certificate_number.$model" />
                    </div>
                    <div class="input-container mb-2 w-full pr-4" data-cy="cover-section-one-field">
                        <ui-label>{{ sectionOneLabel }}</ui-label>
                        <ui-text-area v-model="v$.cover.section_1.$model" />
                    </div>
                    <div class="input-container mb-2 w-full pr-4" data-cy="cover-section-two-field">
                        <ui-label>{{ sectionTwoLabel }}</ui-label>
                        <ui-text-area v-model="v$.cover.section_2.$model" />
                    </div>
                    <div class="input-container mb-2 w-full pr-4" data-cy="cover-section-three-field">
                        <ui-label>{{ sectionThreeLabel }}</ui-label>
                        <ui-text-area v-model="v$.cover.section_3.$model" />
                    </div>
                    <div class="input-container mb-2 w-full pr-4" data-cy="cover-section-four-field">
                        <ui-label>{{ sectionFourLabel }}</ui-label>
                        <ui-text-area v-model="v$.cover.section_4.$model" />
                    </div>
                </div>
            </div>
            <div class="form-section flex flex-col">
                <div
                    class="text-3xl mb-2 pb-2 font-light flex items-end justify-between ml-4 borderline"
                    v-if="!readOnly"
                    data-cy="vehicle-information-form-header"
                    id="vehicle-information"
                >
                    <div>
                        {{ $t('covers.forms.title.vehicle_info') }}
                    </div>
                    <div class="text-default-blue-green">
                        {{
                            $t('covers.forms.title.step', {
                                step_num: 2,
                            })
                        }}
                    </div>
                </div>
            </div>
            <div class="flex w-full flex-col xl:flex-row" data-cy="vehicle-information-form">
                <div
                    class="section-body w-full flex flex-row"
                    :class="display_type == 'page' ? 'ml-4 lg:w-10/12 xl:w-3/4 2xl:w-1/2' : 'p-4'"
                    v-if="!readOnly && !toggle_vehicle_upload_visibility"
                >
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="vehicle-section-registration-number-field">
                        <ui-label>{{ $t('covers.forms.main.registration_number') }}</ui-label>
                        <ui-input
                            v-model="v$.cover.registration_number.$model"
                            :class="{ 'form-error': v$.cover.registration_number.$error }"
                        />
                        <div
                            data-cy="field-vehicle-section-registration-number-error"
                            :class="{ 'text-default-red': v$.cover.registration_number.$error }"
                            v-if="
                                !v$.cover.registration_number.alphaNum.$invalid &&
                                v$.cover.registration_number.requiredIf.$invalid &&
                                v$.cover.registration_number.$error
                            "
                        >
                            {{ $t('validation.required') }}
                        </div>
                        <div
                            data-cy="field-vehicle-section-registration-number-alpha-num-error"
                            :class="{ 'text-default-red': v$.cover.registration_number.$error }"
                            v-if="
                                v$.cover.registration_number.alphaNum.$invalid &&
                                !v$.cover.registration_number.requiredIf.$invalid &&
                                v$.cover.registration_number.$error
                            "
                        >
                            {{ $t('validation.alpha_num', { attribute: 'Registration Number' }) }}
                        </div>
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="vehicle-section-chassis-number-field">
                        <ui-label
                            >{{ $t('covers.forms.main.chassis_number') }}

                            <div
                                class="text-blue-500 float-right underline hover:no-underline cursor-pointer"
                                @click="chassisSearch"
                                data-cy="field-chassis_number_look_up"
                            >
                                {{ $t('covers.forms.main.chassis_number_look_up') }}
                            </div>
                        </ui-label>

                        <ui-input
                            v-model="v$.cover.chassis_number.$model"
                            :class="{ 'form-error': v$.cover.chassis_number.$error || chassis_number_error }"
                        />
                        <div
                            data-cy="field-vehicle-section-chassis-number-error"
                            :class="{ 'text-default-red': v$.cover.chassis_number.$error || chassis_number_error }"
                            v-if="
                                (v$.cover.chassis_number.requiredIf.$invalid &&
                                    v$.cover.chassis_number.$error &&
                                    !v$.cover.chassis_number.alphaNum.$invalid) ||
                                chassis_number_error
                            "
                        >
                            {{ $t('validation.required') }}
                        </div>
                        <div
                            data-cy="field-vehicle-section-chassis-number-alpha-num-error"
                            :class="{ 'text-default-red': v$.cover.chassis_number.$error || chassis_number_error }"
                            v-if="
                                (!v$.cover.chassis_number.requiredIf.$invalid &&
                                    v$.cover.chassis_number.$error &&
                                    v$.cover.chassis_number.alphaNum.$invalid) ||
                                chassis_number_error
                            "
                        >
                            {{ $t('validation.alpha_num', { attribute: 'Chassis Number' }) }}
                        </div>
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="vehicle-section-vehicle-make-field">
                        <ui-label>{{ $t('covers.forms.main.vehicle_make') }}</ui-label>
                        <ui-select-lookup
                            v-model="v$.cover.vehicle_make.$model"
                            :options="vehicle_make"
                            :class="{ 'form-error': v$.cover.vehicle_make.$error }"
                        />
                        <div
                            data-cy="field-vehicle-section-vehicle-make-error"
                            :class="{ 'text-default-red': v$.cover.vehicle_make.$error }"
                            v-if="v$.cover.vehicle_make.requiredIf.$invalid && v$.cover.vehicle_make.$error"
                        >
                            {{ $t('validation.required') }}
                        </div>
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="vehicle-section-vehicle-model-field">
                        <ui-label>{{ $t('covers.forms.main.vehicle_model') }}</ui-label>
                        <ui-select-lookup
                            v-model="v$.cover.vehicle_model.$model"
                            :options="vehicle_model"
                            :class="{ 'form-error': v$.cover.vehicle_model.$error }"
                        />
                        <div
                            data-cy="field-vehicle-section-vehicle-model-error"
                            :class="{ 'text-default-red': v$.cover.vehicle_model.$error }"
                            v-if="v$.cover.vehicle_model.requiredIf.$invalid && v$.cover.vehicle_model.$error"
                        >
                            {{ $t('validation.required') }}
                        </div>
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="vehicle-section-vehicle-year-field">
                        <ui-label>{{ $t('covers.forms.main.vehicle_year') }}</ui-label>
                        <ui-input-number
                            v-model="v$.cover.vehicle_year.$model"
                            :min="1980"
                            :max="max_year"
                            :class="{ 'form-error': v$.cover.vehicle_year.$error }"
                        />
                        <div
                            data-cy="field-vehicle-section-vehicle-year-error"
                            :class="{ 'text-default-red': v$.cover.vehicle_year.$error }"
                            v-if="v$.cover.vehicle_year.requiredIf.$invalid && v$.cover.vehicle_year.$error"
                        >
                            {{ $t('validation.required') }}
                        </div>
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="vehicle-section-vehicle-color-field">
                        <ui-label>{{ $t('covers.forms.main.vehicle_color') }}</ui-label>
                        <ui-input
                            v-model="v$.cover.vehicle_color.$model"
                            :class="{ 'form-error': v$.cover.vehicle_color.$error }"
                        />
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="vehicle-section-engine-number-field">
                        <ui-label>{{ $t('covers.forms.main.engine_number') }}</ui-label>
                        <ui-input
                            v-model="v$.cover.engine_number.$model"
                            :class="{ 'form-error': v$.cover.engine_number.$error }"
                        />
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="vehicle-section-sum-insured-field">
                        <ui-label>{{ $t('covers.forms.main.sum_insured') }}</ui-label>
                        <ui-input-number
                            v-model="v$.cover.sum_insured.$model"
                            :class="{ 'form-error': v$.cover.sum_insured.$error }"
                            :formatter="formatters.currencyFormatter"
                            :parser="formatters.currencyParser"
                            :min="0"
                            :step="0.01"
                        />
                        <div
                            data-cy="field-vehicle-section-sum-insured-error"
                            :class="{ 'text-default-red': v$.cover.sum_insured.$error }"
                            v-if="v$.cover.sum_insured.requiredIf.$invalid && v$.cover.sum_insured.$error"
                        >
                            {{ $t('validation.required') }}
                        </div>
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="vehicle-section-hpcc-field">
                        <ui-label>{{ $t('covers.forms.main.hpcc') }}</ui-label>
                        <ui-input-number
                            v-model="v$.cover.hpcc.$model"
                            :class="{ 'form-error': v$.cover.hpcc.$error }"
                        />
                    </div>
                    <div class="input-container mb-2 w-1/2 pr-4" data-cy="vehicle-section-seating-field">
                        <ui-label>{{ $t('covers.forms.main.seating') }}</ui-label>
                        <ui-input-number
                            v-model="v$.cover.seating.$model"
                            :min="0"
                            :max="100"
                            :class="{ 'form-error': v$.cover.seating.$error }"
                        />
                    </div>
                </div>
                <div
                    class="flex flex-row"
                    data-cy="field-vehicle-section-upload-vehicle-data-field"
                    :class="{
                        'flex-row py-8 xl:w-1/4 2xl:w-1/2 ml-4 xl:ml-0': !toggle_vehicle_upload_visibility,
                        'w-full pt-2 pb-12 ml-4 ml-4 ': toggle_vehicle_upload_visibility,
                    }"
                    v-if="this.cover_id == '' && section_type == 'cover'"
                >
                    <a-upload-dragger
                        :class="{
                            'w-full': toggle_vehicle_upload_visibility,
                        }"
                        name="file"
                        data-cy="vehicle-import-drag"
                        @remove="handleUploadRemove"
                        :file-list="file_list"
                        :multiple="false"
                        :before-upload="beforeUploadFile"
                    >
                        <p class="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p class="ant-upload-text">{{ $t('covers.forms.upload.body') }}</p>
                        <p class="ant-upload-hint">{{ $t('covers.forms.upload.hint') }}</p>
                    </a-upload-dragger>
                </div>
            </div>
            <div class="form-section flex flex-col">
                <div
                    class="text-3xl mb-2 pb-2 font-light flex items-end justify-between ml-4 borderline mb-3"
                    data-cy="policy-holder-form-header"
                    id="policy-holders"
                >
                    <div>
                        {{ $t('covers.forms.title.policy_holder') }}
                    </div>
                    <div class="text-default-blue-green" v-if="!readOnly">
                        {{
                            $t('covers.forms.title.step', {
                                step_num: 3,
                            })
                        }}
                    </div>
                </div>
                <policy-names-table
                    class="pl-4 mb-4"
                    :class="{ 'pr-4': display_type == 'drawer' }"
                    :cover="cover"
                    :is_view="readOnly"
                    :policy_name_data="policy_name_list"
                    :is_driver="toggle_vehicle_upload_visibility"
                    @change="onChangePolicyName"
                    @add="onSavePolicyName"
                    ref="PolicyNameTable"
                />
            </div>
            <div class="form-section flex flex-col" v-if="!toggle_vehicle_upload_visibility">
                <div
                    class="text-3xl mb-2 pb-2 font-light flex items-end justify-between ml-4 borderline mb-3"
                    data-cy="vehicle-drivers-form-header"
                    id="vehicle-drivers"
                >
                    <div>
                        {{ $t('covers.forms.title.vehicle_drivers') }}
                    </div>
                    <div class="text-default-blue-green" v-if="!readOnly">
                        {{
                            $t('covers.forms.title.step', {
                                step_num: 4,
                            })
                        }}
                    </div>
                </div>
                <vehicle-drivers-table
                    class="pl-4 mb-4"
                    :class="{ 'pr-4': display_type == 'drawer' }"
                    :cover="cover"
                    :is_view="readOnly"
                    :policy_name_data="policy_name_list"
                    @change="onChangePolicyName"
                    @add="onSavePolicyName"
                    ref="VehicleDriversTable"
                />
            </div>
            <div class="form-section flex flex-col" v-if="form_mode == 'edit'">
                <div
                    class="text-3xl mb-2 pb-2 font-light flex items-end justify-between ml-4 borderline mb-3"
                    data-cy="audit-info-form-header"
                    id="audit-info"
                >
                    <div>
                        {{ $t('covers.forms.title.audit_info') }}
                    </div>
                </div>
            </div>
            <div class="form-section flex flex-col" v-if="form_mode == 'edit'">
                <div
                    class="section-body w-full flex flex-row w-full"
                    :class="display_type == 'page' ? 'ml-4 lg:w-10/12 xl:w-3/4 2xl:w-1/2' : 'p-4'"
                >
                    <div class="flex w-full lg:w-1/2 px-2 my-2" data-cy="created-by-field">
                        <ui-label class="w-1/2">
                            {{ $t('covers.forms.main.created_by') }}
                        </ui-label>
                        <div class="subText w-1/2 md:w-2/3">
                            {{ cover.created_by }}
                        </div>
                    </div>
                    <div class="flex w-full lg:w-1/2 px-2 my-2" data-cy="created-at-field">
                        <ui-label class="w-1/2">
                            {{ $t('covers.forms.main.created_at') }}
                        </ui-label>
                        <div class="subText w-1/2 md:w-2/3">
                            {{ cover.created_at }}
                        </div>
                    </div>
                    <div class="flex w-full lg:w-1/2 px-2 my-2" data-cy="updated-by-field">
                        <ui-label class="w-1/2">
                            {{ $t('covers.forms.main.updated_by') }}
                        </ui-label>
                        <div class="subText w-1/2 md:w-2/3">
                            {{ cover.last_updated_by }}
                        </div>
                    </div>
                    <div class="flex w-full lg:w-1/2 px-2 my-2" data-cy="updated-at-field">
                        <ui-label class="w-1/2">
                            {{ $t('covers.forms.main.updated_at') }}
                        </ui-label>
                        <div class="subText w-1/2 md:w-2/3">
                            {{ cover.updated_at }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="form-section flex flex-col" v-if="form_mode == 'edit'">
                <div
                    class="text-3xl mb-2 pb-2 font-light flex items-end justify-between ml-4 borderline mb-3"
                    data-cy="print-info-form-header"
                    id="print-info"
                >
                    <div>
                        {{ $t('covers.forms.title.print_info') }}
                    </div>
                </div>
            </div>
            <div class="form-section flex flex-col" v-if="form_mode == 'edit'">
                <div
                    class="section-body w-full flex flex-row w-full"
                    :class="display_type == 'page' ? 'ml-4 lg:w-10/12 xl:w-3/4 2xl:w-1/2' : 'p-4'"
                >
                    <div class="flex w-full lg:w-1/2 px-2 my-2" data-cy="printed-first-at-field">
                        <ui-label class="w-1/2">
                            {{ $t('covers.forms.main.printed_first_at') }}
                        </ui-label>
                        <div class="subText w-1/2 md:w-2/3">
                            {{ cover.printed_first_at }}
                        </div>
                    </div>
                    <div class="flex w-full lg:w-1/2 px-2 my-2" data-cy="printed-last-at-field">
                        <ui-label class="w-1/2">
                            {{ $t('covers.forms.main.printed_last_at') }}
                        </ui-label>
                        <div class="subText w-1/2 md:w-2/3">
                            {{ cover.printed_last_at }}
                        </div>
                    </div>
                    <div class="flex w-full lg:w-1/2 px-2 my-2" data-cy="printed-last-by-field">
                        <ui-label class="w-1/2">
                            {{ $t('covers.forms.main.printed_last_by') }}
                        </ui-label>
                        <div class="subText w-1/2 md:w-2/3">
                            {{ cover.printed_last_by }}
                        </div>
                    </div>
                    <div class="flex w-full lg:w-1/2 px-2 my-2" data-cy="print-count-field">
                        <ui-label class="w-1/2">
                            {{ $t('covers.forms.main.print_count') }}
                        </ui-label>
                        <div class="subText w-1/2 md:w-2/3">
                            {{ cover.print_count }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="flex flex-row lg:flex-row justify-end py-3.5">
                <a-button data-cy="back-button" @click="onCancel" class="ant-btn mr-2" :disabled="processing">
                    {{ $t('covers.forms.main.buttons.back') }}
                </a-button>
                <a-button
                    data-cy="download-import-file-button"
                    @click="downloadImportFile"
                    class="ant-btn mr-2"
                    v-if="canDownloadImportFile"
                    :disabled="processing"
                    :loading="download_processing"
                >
                    {{ $t('covers.forms.main.buttons.download_import_file') }}
                </a-button>
                <a-button
                    data-cy="duplicate-button-fleet"
                    @click="onDuplicate('fleet')"
                    class="ant-btn mr-2"
                    v-if="canDuplicate"
                    :disabled="processing"
                    :loading="duplicate_processing"
                >
                    {{ $t('covers.forms.main.buttons.duplicate_fleet') }}
                </a-button>
                <a-button
                    data-cy="duplicate-button-cover"
                    @click="onDuplicate('cover')"
                    class="ant-btn mr-2"
                    v-if="canDuplicate"
                    :disabled="processing"
                    :loading="duplicate_processing"
                >
                    {{ $t('covers.forms.main.buttons.duplicate_cover') }}
                </a-button>
                <a-button
                    v-if="!cover.is_authorized && isAnAuthorizer"
                    data-cy="authorize-button"
                    @click="onAuthorize"
                    class="ant-btn mr-2"
                    :disabled="processing"
                    :loading="authorize_processing"
                >
                    {{ $t('covers.forms.main.buttons.authorize') }}
                </a-button>
                <!-- <a-button
                    v-if="cover.is_authorized && cover.cover_status == 'active'"
                    class="ant-btn mr-2"
                    data-cy="print-preview-button"
                    @click="printCover(true)"
                    :disabled="processing"
                    :loading="print_processing"
                >
                    {{ $t('covers.table.actions.print_preview') }}
                </a-button> -->
                <a-button
                    v-if="cover.is_authorized && cover.cover_status == 'active'"
                    class="ant-btn mr-2"
                    data-cy="print-button"
                    @click="printCover()"
                    :disabled="processing"
                    :loading="print_processing"
                >
                    {{ $t('covers.table.actions.print') }}
                </a-button>
                <ui-submit-button v-if="!readOnly" data-cy="save-btn" :disabled="processing">
                    <span v-if="!processing">{{ $t('covers.forms.main.buttons.save') }}</span>
                    <span v-else>{{ $t('covers.forms.main.buttons.saving') }}</span>
                </ui-submit-button>
            </div>
        </form>
        <modal-templates
            @select="handleSelect"
            :company_id="cover.insurer_id"
            ref="TemplatesModal"
            section_type="cover"
            :user_type="form_type == 'insurer' ? 'admin' : 'user'"
        />
    </div>
</template>

<script>
import UiCheckbox from '@/components/ui/Checkbox.vue';
import UiDateRangePickerDouble from '@/components/ui/DateRangePickerDouble.vue';
import UiInput from '@/components/ui/Input.vue';
import UiInputNumber from '@/components/ui/InputNumber.vue';
import UiLabel from '@/components/ui/Label.vue';
import UiSelect from '@/components/ui/Select.vue';
import UiSelectLookup from '@/components/ui/SelectLookup.vue';
import UiSubmitButton from '@/components/ui/SubmitButton.vue';
import UiTextArea from '@/components/ui/TextArea.vue';
import PolicyNamesTable from '@/components/tables/PolicyNames.vue';
import VehicleDriversTable from '@/components/tables/VehicleDrivers.vue';
import ModalTemplates from '@/components/modals/Templates.vue';

import ValidationErrors from '@/components/ui/ValidationErrors.vue';

import { InboxOutlined, SearchOutlined } from '@ant-design/icons-vue';

import { useVuelidate } from '@vuelidate/core';
import { required, alphaNum, requiredIf } from '@vuelidate/validators';

import FileManager from '@/utils/file.js';
import formatters from '@/utils/formatters.js';

export default {
    setup() {
        return { v$: useVuelidate() };
    },
    components: {
        UiCheckbox,
        UiDateRangePickerDouble,
        UiInput,
        UiLabel,
        UiSelect,
        UiSelectLookup,
        UiSubmitButton,
        UiTextArea,
        ValidationErrors,
        PolicyNamesTable,
        VehicleDriversTable,
        UiInputNumber,
        ModalTemplates,
        InboxOutlined,
        SearchOutlined,
    },
    validations: {
        cover: {
            insurer_id: { required },
            company_id: { required },
            insurer_policy_number: {
                requiredIf: requiredIf(function () {
                    if (this.cover.cover_type === 'certificate') {
                        return true;
                    } else {
                        return false;
                    }
                }),
            },
            broker_policy_number: {},
            policy_start_date: { required },
            policy_end_date: { required },
            is_motor_trade: {},
            cover_effective_date: { required },
            cover_expiry_date: { required },
            policy_date_range: { required },
            cover_date_range: { required },
            days_of_cover: {},
            certificate_type: { required },
            policyholder_address: {},
            cover_type: { required },
            manual_certificate_number: {},
            risk_mortgagee: {},
            cover_category: {},
            section_1_label: {},
            section_1: {},
            section_2_label: {},
            section_2: {},
            section_3_label: {},
            section_3: {},
            section_4_label: {},
            section_4: {},
            registration_number: {
                requiredIf: requiredIf(function () {
                    return this.toggle_vehicle_upload_visibility ? false : this.cover.is_motor_trade;
                }),
                alphaNum,
            },
            chassis_number: {
                requiredIf: requiredIf(function () {
                    return this.toggle_vehicle_upload_visibility ? false : !this.cover.is_motor_trade;
                }),
                alphaNum,
            },
            vehicle_make: {
                requiredIf: requiredIf(function () {
                    return this.toggle_vehicle_upload_visibility ? false : !this.cover.is_motor_trade;
                }),
            },
            vehicle_model: {
                requiredIf: requiredIf(function () {
                    return this.toggle_vehicle_upload_visibility ? false : !this.cover.is_motor_trade;
                }),
            },
            vehicle_year: {
                requiredIf: requiredIf(function () {
                    return this.toggle_vehicle_upload_visibility ? false : !this.cover.is_motor_trade;
                }),
            },
            vehicle_color: {},
            engine_number: {},
            sum_insured: {
                requiredIf: requiredIf(function () {
                    return this.toggle_vehicle_upload_visibility ? false : !this.cover.is_motor_trade;
                }),
            },
            hpcc: {},
            seating: {},
        },
    },
    computed: {
        anchorItems() {
            let anchor_items = this.anchor_items;

            if (this.form_mode !== 'edit') {
                anchor_items[0].children.splice(4, 1);
            }

            return anchor_items;
        },
        defaultRow() {
            return this.section_type == 'renew' ? 4 : 6;
        },
        sectionOneLabel() {
            return this.cover.section_1_label === undefined ||
                this.cover.section_1_label === '' ||
                this.cover.section_1_label === null
                ? this.$t('covers.forms.main.section_1')
                : this.cover.section_1_label;
        },
        sectionTwoLabel() {
            return this.cover.section_2_label === undefined ||
                this.cover.section_2_label === '' ||
                this.cover.section_2_label === null
                ? this.$t('covers.forms.main.section_2')
                : this.cover.section_2_label;
        },
        sectionThreeLabel() {
            return this.cover.section_3_label === undefined ||
                this.cover.section_3_label === '' ||
                this.cover.section_3_label === null
                ? this.$t('covers.forms.main.section_3')
                : this.cover.section_3_label;
        },
        sectionFourLabel() {
            return this.cover.section_4_label === undefined ||
                this.cover.section_4_label === '' ||
                this.cover.section_4_label === null
                ? this.$t('covers.forms.main.section_4')
                : this.cover.section_4_label;
        },
        isAnAuthorizer() {
            return this.$store.state.user.abilities.includes('authorize-covers');
        },
        isAdminRole() {
            return (
                this.$store.state.user.abilities.includes('manage-association') ||
                this.$store.state.user.abilities.includes('manage-admin')
            );
        },
        canDuplicate() {
            return (
                this.cover &&
                this.cover.id !== '' &&
                this.cover.id !== undefined &&
                this.cover.cover_status &&
                this.cover.cover_status.toLowerCase() == 'active' &&
                this.$dayjs(this.cover.cover_expiry_date).diff(this.$dayjs()) >= 0 &&
                !this.readOnly &&
                !this.isAdminRole // no admin role should be able to cascade
            );
        },
        canDownloadImportFile() {
            return !_.isEmpty(this.cover.file_source) && this.isAdminRole;
        },
        readOnly() {
            return this.is_view || this.forced_view;
        },
    },
    data() {
        return {
            anchor_items: [
                {
                    key: '1',
                    href: '#',
                    title: this.formTitle(),
                    children: [
                        {
                            key: '2',
                            href: '#cover-information',
                            title: this.$t('covers.forms.title.cover_info'),
                        },
                        {
                            key: '3',
                            href: '#vehicle-information',
                            title: this.$t('covers.forms.title.vehicle_info'),
                        },
                        {
                            key: '4',
                            href: '#policy-holders',
                            title: this.$t('covers.forms.title.policy_holder'),
                        },
                        {
                            key: '5',
                            href: '#vehicle-drivers',
                            title: this.$t('covers.forms.title.vehicle_drivers'),
                        },
                        {
                            key: '6',
                            href: '#audit-info',
                            title: this.$t('covers.forms.title.audit_info'),
                        },
                        {
                            key: '7',
                            href: '#print-info',
                            title: this.$t('covers.forms.title.print_info'),
                        },
                    ],
                },
            ],
            insurer_list: [],
            intermediaries_list: [],
            policy_name_list: [],
            cover_types: [],
            days: '',
            risk_mortgagee_list: [],
            cover_category_list: [],
            is_trn_warned: false,
            vehicle_make: [],
            vehicle_model: [],
            max_year: this.$dayjs().add(1, 'years').year(),
            form_type: 'insurer',
            processing: false,
            duplicate_processing: false,
            print_processing: false,
            authorize_processing: false,
            download_processing: false,
            loaded: false,
            formatters,
            file_list: [],
            chassis_number_error: false,
            cover_date_limits: {
                start_date: null,
                end_date: null,
            },
            toggle_vehicle_upload_visibility: false,
            file_limit: -1,
            forced_view: false,
        };
    },
    mounted() {
        this.fetch();

        this.cover_date_limits = {
            start_date: this.cover.policy_date_range.start_date,
            end_date: this.cover.policy_date_range.end_date,
        };

        this.dateDifference({
            start_date: this.cover.cover_date_range.start_date.format('YYYY-MM-DD HH:mm:ss'),
            end_date: this.cover.cover_date_range.end_date.format('YYYY-MM-DD HH:mm:ss'),
        });
    },
    props: {
        cover: Object,
        cover_id: {
            type: String,
            default: '',
        },
        renew_id: {
            type: [String, Number],
            default: '',
        },
        validation: Object,
        form_mode: {
            type: String,
            default: 'init',
        },
        display_type: {
            type: String,
            default: 'page',
        },
        section_type: {
            type: String,
            default: 'cover',
        },
        is_view: {
            type: Boolean,
            default: false,
        },
    },
    methods: {
        formTitle() {
            return this.section_type == 'renew'
                ? this.$t('covers.forms.title.renew')
                : this.cover_id == ''
                  ? this.$t('covers.forms.title.create')
                  : this.$t('covers.forms.title.edit');
        },
        isFormDirty() {
            this.$emit('is-form-dirty', this.v$.$anyDirty);
        },
        handleSubmit() {
            this.v$.$touch();

            if (this.policy_name_list.filter((record) => record.is_a_policy_holder == 1).length == 0) {
                this.$notification.error({
                    message: this.$t('covers.notifications.error.policy_name_or_driver_required'),
                });
            }

            if (
                this.v$.$invalid ||
                this.policy_name_list.filter((record) => record.is_a_policy_holder == 1).length == 0
            ) {
                return false;
            }

            this.$emit('formSubmit');
        },
        addMonths(value) {
            const m = this.$dayjs(this.cover.policy_start_date).endOf('day');

            this.cover.policy_end_date = m.add(value, 'months').subtract(1, 'day');
            this.$refs.policyDateDouble.onEndDateChange(m, this.cover.policy_end_date.format('YYYY-MM-DD HH:mm:ss'));

            this.cover.policy_date_range = {
                ...this.cover.policy_date_range,
                end_date: this.cover.policy_end_date,
            };
        },
        updateDatePolicy() {
            this.cover.policy_start_date = this.cover.policy_date_range.start_date;
            this.cover.policy_end_date = this.cover.policy_date_range.end_date;

            this.restrictDate();
        },
        restrictDate() {
            const policy_start_date = this.$dayjs(this.cover.policy_start_date);
            const policy_end_date = this.$dayjs(this.cover.policy_end_date);

            if (policy_start_date.isAfter(this.cover.cover_effective_date)) {
                this.cover.cover_effective_date = policy_start_date;
            }

            if (this.cover.cover_type === 'covernote') {
                this.cover.cover_expiry_date = policy_start_date
                    .clone()
                    .add(1, 'months')
                    .subtract(1, 'day')
                    .endOf('day');
            } else {
                const max_date = this.cover.cover_effective_date.clone().add(366, 'days');

                if (max_date.isBefore(policy_end_date)) {
                    this.cover.cover_expiry_date = max_date;
                } else {
                    this.cover.cover_expiry_date = policy_end_date;
                }
            }

            this.$refs.coverDateDouble.onEndDateChange('', this.cover.cover_expiry_date);
            this.$refs.coverDateDouble.onStartDateChange('', this.cover.cover_effective_date);

            this.cover_date_limits = {
                start_date: this.cover.cover_effective_date,
                end_date: this.cover.cover_expiry_date,
            };
        },
        dateDifference() {
            this.cover.cover_effective_date = this.cover.cover_date_range.start_date;
            this.cover.cover_expiry_date = this.cover.cover_date_range.end_date;

            if (
                this.cover.cover_date_range.start_date == '' ||
                this.cover.cover_date_range.start_date == null ||
                this.cover.cover_date_range.end_date == '' ||
                this.cover.cover_date_range.end_date == null
            ) {
                return (this.days = 0);
            } else {
                var dateA = this.cover.cover_date_range.start_date;
                var dateB = this.cover.cover_date_range.end_date;
                this.days = dateB.diff(dateA, 'days');
            }
        },
        onChangePolicyName(value) {
            switch (value.type) {
                case 'create':
                    this.policy_name_list = value.data;

                    if (value.is_driver) {
                        this.$notification.success({
                            message: this.$t('covers.forms.policy_holder.notifications.success.create_vehicle_drivers'),
                        });
                    } else {
                        this.$notification.success({
                            message: this.$t('covers.forms.policy_holder.notifications.success.create_policy_holder'),
                        });
                    }
                    break;
                case 'update':
                    this.policy_name_list = value.data;

                    if (value.is_driver) {
                        this.$notification.success({
                            message: this.$t('covers.forms.policy_holder.notifications.success.update_vehicle_drivers'),
                        });
                    } else {
                        this.$notification.success({
                            message: this.$t('covers.forms.policy_holder.notifications.success.update_policy_holder'),
                        });
                    }
                    break;
                case 'remove':
                    this.policy_name_list = value.data;
                    break;
                case 'cancel':
                    this.is_trn_warned = false;
                    break;
                case 'reset':
                    this.policy_name_list = [];

                    if (this.cover_id != '') {
                        this.$http
                            .get('/api/policy_names/' + this.cover_id)
                            .then((response) => {
                                response.data.data.forEach((record) => {
                                    if (record.is_a_driver != 1) {
                                        this.policy_name_list.push({
                                            ...record,
                                            is_a_company: record.is_a_company ? true : false,
                                            is_a_policy_holder: record.is_a_policy_holder ? true : false,
                                            is_a_driver: record.is_a_driver ? true : false,
                                            is_excluded: record.is_excluded ? true : false,
                                            is_main_driver: record.is_main_driver ? true : false,
                                        });
                                    }
                                });
                                this.$notification.success({
                                    message: this.$t('covers.forms.policy_holder.notifications.success.reset'),
                                });
                            })
                            .catch((error) => {
                                this.$notification.error({
                                    message: this.$t('globals.global.notifications.error.any', { code: 'CVCC02' }),
                                });
                            });
                    } else {
                        this.$notification.success({
                            message: this.$t('covers.forms.policy_holder.notifications.success.reset'),
                        });
                    }
                    break;
            }
        },
        fetch() {
            this.loaded = false;
            let urls = [this.$http.get('/api/cia/get-insurers-and-intermediaries')];

            if (this.renew_id !== '') {
                urls.push(this.$http.get('/api/policy_names/' + this.renew_id));
            } else if (this.cover_id !== '') {
                urls.push(this.$http.get('/api/policy_names/' + this.cover_id));

                this.insurer_list.push({
                    text: this.cover.insurer_name,
                    value: this.cover.insurer_id,
                });
            }

            if (urls.length == 1) {
                this.$http.all(urls).then(
                    this.$http.spread((insurers) => {
                        this.insurer_list = [];
                        this.intermediaries_list = [];
                        this.policy_name_list = [];

                        this.form_type = insurers.data.data.type;

                        if (insurers.data.data.type == 'insurer') {
                            insurers.data.data.intermediaries.forEach((record) => {
                                this.intermediaries_list.push({
                                    text: record.name,
                                    value: record.company_intermediary_id,
                                });
                            });
                        } else {
                            insurers.data.data.intermediaries.forEach((record) => {
                                this.intermediaries_list.push({
                                    text: record.name,
                                    value: record.company_id,
                                });
                            });
                        }

                        insurers.data.data.insurers.forEach((record) => {
                            this.insurer_list.push({
                                text: record.name,
                                value: record.company_id,
                            });
                        });

                        if (insurers.data.data.type == 'insurer' && this.cover.insurer_id == '') {
                            this.cover.insurer_id = insurers.data.data.insurers[0].company_id;
                        }

                        this.intermediaries_list = _.sortBy(this.intermediaries_list, ['text', 'value']);

                        this.setCompanyId();

                        this.loaded = true;
                    }),
                );
            } else {
                this.$http.all(urls).then(
                    this.$http.spread((insurers_intermediaries, policy_names) => {
                        this.intermediaries_list = [];
                        this.policy_name_list = [];

                        let intermediary_found = false;

                        if (insurers_intermediaries.data.status == 'failed') {
                            this.forced_view = true;

                            this.$notification.error({
                                message: insurers_intermediaries.data.message,
                            });
                        } else {
                            this.form_type = insurers_intermediaries.data.data.type;

                            if (insurers_intermediaries.data.data.type == 'insurer') {
                                insurers_intermediaries.data.data.intermediaries.forEach((record) => {
                                    if (record.company_intermediary_id == this.cover.intermediary_id) {
                                        intermediary_found = true;
                                    }
                                    this.intermediaries_list.push({
                                        text: record.name,
                                        value: record.company_intermediary_id,
                                    });
                                });
                            } else {
                                insurers_intermediaries.data.data.intermediaries.forEach((record) => {
                                    this.intermediaries_list.push({
                                        text: record.name,
                                        value: record.company_id,
                                    });
                                });

                                this.insurer_list = [];

                                insurers_intermediaries.data.data.insurers.forEach((record) => {
                                    this.insurer_list.push({
                                        text: record.name,
                                        value: record.company_id,
                                    });
                                });
                            }
                        }

                        let that = this;

                        policy_names.data.data.forEach((record, increment) => {
                            that.policy_name_list.push({
                                ...record,
                                id: that.renew_id !== '' ? 'new_' + increment : record.id,
                                is_a_company: record.is_a_company ? true : false,
                                is_a_policy_holder: record.is_a_policy_holder ? true : false,
                                is_a_driver: record.is_a_driver ? true : false,
                                is_excluded: record.is_excluded ? true : false,
                                is_main_driver: record.is_main_driver ? true : false,
                            });
                        });

                        if (!intermediary_found) {
                            this.intermediaries_list = [
                                {
                                    text: this.cover.intermediary_name,
                                    value: this.cover.company_id,
                                },
                            ];
                        }

                        this.intermediaries_list = _.sortBy(this.intermediaries_list, ['text', 'value']);

                        this.setCompanyId();

                        this.loaded = true;
                    }),
                );
            }

            this.postFetch();
        },
        postFetch() {
            let urls = [
                this.$http.get('/api/listmanager/category/vehicle_make'),
                this.$http.get('/api/listmanager/category/vehicle_model'),
                this.$http.get('/api/listmanager/category/cover_type'),
                this.$http.get('/api/listmanager/category/cover_category'),
            ];

            this.$http.all(urls).then(
                this.$http.spread((vehicle_make, vehicle_model, cover_type, cover_category) => {
                    this.setupLists(vehicle_make, vehicle_model, cover_type, cover_category);
                }),
            );
        },
        refreshPolicyHoldersList() {
            if (this.cover_id != '') {
                this.$http
                    .get('/api/policy_names/' + this.cover_id)
                    .then((response) => {
                        this.policy_name_list = [];

                        response.data.data.forEach((record) => {
                            this.policy_name_list.push({
                                ...record,
                                is_a_company: record.is_a_company ? true : false,
                                is_a_policy_holder: record.is_a_policy_holder ? true : false,
                                is_a_driver: record.is_a_driver ? true : false,
                                is_excluded: record.is_excluded ? true : false,
                                is_main_driver: record.is_main_driver ? true : false,
                            });
                        });
                        this.$notification.success({
                            message: this.$t('covers.forms.policy_holder.notifications.success.reset'),
                        });
                    })
                    .catch((error) => {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVCC02' }),
                        });
                    });
            } else {
                this.$notification.success({
                    message: this.$t('covers.forms.policy_holder.notifications.success.reset'),
                });
            }
        },
        setupLists(vehicle_make, vehicle_model, cover_type, cover_category) {
            this.vehicle_make = [];
            this.vehicle_model = [];
            this.cover_types = [];
            this.cover_category_list = [];

            vehicle_make.data.data.forEach((record) => {
                this.vehicle_make.push({
                    text: record.value,
                    value: record.value,
                });
            });

            vehicle_model.data.data.forEach((record) => {
                this.vehicle_model.push({
                    text: record.value,
                    value: record.value,
                });
            });

            cover_type.data.data.forEach((record) => {
                this.cover_types.push({
                    text: record.item,
                    value: record.value,
                });
            });

            cover_category.data.data.forEach((record) => {
                this.cover_category_list.push({
                    text: record.item,
                    value: record.value,
                });
            });

            this.vehicle_make = _.uniqBy(this.vehicle_make, 'value');
            this.vehicle_model = _.uniqBy(this.vehicle_model, 'value');
        },
        chassisSearch() {
            if (this.cover.chassis_number == '') {
                this.chassis_number_error = true;
            } else {
                let data = {
                    chassis_no: this.cover.chassis_number,
                    certificate_no: '',
                    covernote_no: '',
                    reg_no: '',
                    engine_no: '',
                    limit: 1,
                    sortField: 'chassis_number',
                    sortOrder: 'ascend',
                };

                this.$http
                    .post('/api/v1/covers/search', data)
                    .then((response) => {
                        if (response.data.data.length == 0) {
                            this.$notification.error({
                                message: this.$t('covers.notifications.error.chassis_not_found'),
                            });
                        } else {
                            let vehicle_data = {
                                engine_number: response.data.data[0].engine_number,
                                registration_number: response.data.data[0].registration_number,
                                vehicle_make: response.data.data[0].vehicle_make,
                                vehicle_model: response.data.data[0].vehicle_model,
                                vehicle_year: response.data.data[0].vehicle_year,
                            };
                            this.$emit('update', vehicle_data);
                        }
                    })
                    .catch((error) => {
                        if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVSH01' }),
                            });
                        }
                    })
                    .finally(() => {
                        this.processing = false;
                    });
            }
        },
        onSavePolicyName(data) {
            if (this.processing) {
                return false;
            }

            this.$refs.PolicyNameTable.drawer_form_dirty = false;
            this.processing = true;

            let is_driver = data.is_a_driver ? true : false;

            let params = {
                name: data.name,
                national_id: data.national_id,
            };

            this.$http
                .get('/api/policy_names/name/is-valid', { params: params })
                .then((response) => {
                    let filter_policy_names = this.policy_name_list.filter(
                        (record) => record.national_id == data.national_id,
                    );

                    if (data.id == null) {
                        if (filter_policy_names.length > 0) {
                            this.$notification.error({
                                message: this.$t('covers.notifications.error.trn'),
                            });
                        } else if (response.data.status == 'error' && !this.is_trn_warned) {
                            this.is_trn_warned = true;
                            this.$notification.warning({
                                message: this.$t('covers.notifications.error.trn_warning'),
                            });
                        } else {
                            this.is_trn_warned = false;
                            if (data.is_main_driver) {
                                this.policy_name_list = this.policy_name_list.map((record) => {
                                    if (record.is_main_driver == true) {
                                        record.is_main_driver = false;
                                    }
                                    return record;
                                });
                            }
                            this.policy_name_list.unshift({ ...data, id: 'new_' + this.policy_name_list.length });
                            this.onChangePolicyName({
                                type: 'create',
                                data: this.policy_name_list,
                                is_driver: is_driver,
                            });
                            this.$refs.PolicyNameTable.drawer_visible = false;
                            if (!this.toggle_vehicle_upload_visibility) {
                                this.$refs.VehicleDriversTable.drawer_visible = false;
                            }
                        }
                    } else {
                        if (filter_policy_names.length > 0 && filter_policy_names[0].id != data.id) {
                            this.$notification.error({
                                message: this.$t('covers.notifications.error.trn'),
                            });
                        } else if (response.data.status == 'error' && !this.is_trn_warned) {
                            this.is_trn_warned = true;
                            this.$notification.warning({
                                message: this.$t('covers.notifications.error.trn_warning'),
                            });
                        } else {
                            this.is_trn_warned = false;
                            this.onChangePolicyName({
                                type: 'update',
                                data: this.policy_name_list.map((record) => {
                                    if (
                                        record.is_main_driver == true &&
                                        data.is_main_driver == true &&
                                        data.id != record.id
                                    ) {
                                        record.is_main_driver = false;
                                    }
                                    if (record.id == data.id) {
                                        record = data;
                                    }
                                    return record;
                                }),
                                is_driver: is_driver,
                            });
                            this.$refs.PolicyNameTable.drawer_visible = false;
                            if (!this.toggle_vehicle_upload_visibility) {
                                this.$refs.VehicleDriversTable.drawer_visible = false;
                            }
                        }
                    }
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVCC04' }),
                    });
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        onCancel() {
            this.$emit('cancel');
        },
        handleClick(e, link) {
            e.preventDefault();
        },
        showTemplatesModal() {
            this.$refs.TemplatesModal.showModal();
        },
        handleSelect(record) {
            let { id, ...data } = record;
            this.$emit('update', {
                ...this.cover,
                ...data,
            });
        },
        printCover(is_preview = false) {
            if (this.cover_id === '' || this.cover_id === null || this.cover_id === undefined) {
                return false;
            }

            this.processing = true;
            this.print_processing = true;

            let $url = '/api/covers/' + this.cover_id + '/generate';

            if (is_preview) {
                $url = '/api/covers/' + this.cover_id + '/generate-preview';
            }

            this.$http
                .post($url)
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.fetch();

                        this.$notification.success({
                            message: response.data.message,
                        });

                        this.$emit('refresh');

                        this.downloadReport(response.data.file_path, response.data.file_name);
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVCS02' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                    this.print_processing = false;
                });
        },
        downloadReport(file_path, file_name) {
            this.$http
                .get(file_path, { responseType: 'arraybuffer' })
                .then((response) => {
                    let blob = new Blob([response.data], { type: response.headers['content-type'] });
                    let link = document.createElement('a');

                    link.href = window.URL.createObjectURL(blob);
                    link.download = file_name;

                    link.click();
                })
                .catch((error) => {
                    // was not able to download file
                    console.log(error);
                });
        },
        downloadImportFile() {
            if (this.cover_id === '' || this.cover_id === null || this.cover_id === undefined) {
                return false;
            }

            this.processing = true;
            this.download_processing = true;

            this.$http
                .post('/api/covers/' + this.cover_id + '/download-import-file')
                .then((response) => {
                    if (response.headers['content-type'] == 'application/json') {
                        this.$notification.success({
                            message: this.$t('covers.notifications.success.download_import_file'),
                        });

                        FileManager.download(response);
                    } else {
                        this.$notification.error({ message: response.data.message });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVCDI01' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                    this.download_processing = false;
                });
        },
        updateRisks(is_update = false) {
            this.risk_mortgagee_list = [];
            if (is_update) {
                this.cover.insurer_policy_number = '';
            }

            if (this.cover.insurer_id !== '') {
                this.$http.get('/api/covers/get-mortgagees/' + this.cover.insurer_id).then((response) => {
                    response.data.forEach((record) => {
                        this.risk_mortgagee_list.push({
                            text: record.value,
                            value: record.value,
                        });
                    });
                });
            }
        },
        dayjs: function (date) {
            return this.$dayjs(date);
        },
        onDuplicate(type = 'fleet') {
            if (this.cover_id === '' || this.cover_id === null || this.cover_id === undefined) {
                return false;
            }

            let url = null;

            if (type == 'cover') {
                url = '/api/covers/' + this.cover_id + '/duplicate-cover';
            } else {
                url = '/api/covers/' + this.cover_id + '/duplicate-fleet';
            }

            this.processing = true;
            this.duplicate_processing = true;

            this.$http
                .post(url)
                .then((response) => {
                    if (response.data.status == 'success') {
                        const vm = this;

                        this.$confirm({
                            title: this.$t('covers.modal.duplicate_title'),
                            content: this.$t('covers.modal.duplicate_content'),
                            onOk() {
                                vm.$router
                                    .push({
                                        name: 'edit-cover',
                                        params: {
                                            id: response.data.cover_id,
                                        },
                                    })
                                    .finally(() => {
                                        // this is a temporary fix.
                                        window.location.reload();
                                    });
                            },
                        });
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVCD01' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                    this.duplicate_processing = false;
                });
        },
        onAuthorize() {
            if (this.cover_id === '' || this.cover_id === null || this.cover_id === undefined) {
                return false;
            }

            this.processing = true;
            this.authorize_processing = true;

            this.$http
                .post('/api/covers/' + this.cover_id + '/authorize')
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$emit('refresh');

                        this.$notification.success({
                            message: response.data.message,
                        });
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVCD02' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                    this.authorize_processing = false;
                });
        },
        setCompanyId() {
            if (this.cover_id == '' && this.form_type != 'insurer') {
                this.cover.company_id = this.$store.state.user.company.id;
            }

            this.updateRisks(false);
        },
        handleUploadRemove(file) {
            const index = this.file_list.indexOf(file);
            const new_file_list = this.file_list.slice();
            new_file_list.splice(index, 1);
            this.file_list = new_file_list;
            if (this.file_list.length < 1) {
                this.toggle_vehicle_upload_visibility = false;
            }
        },
        beforeUploadFile(file) {
            const isCSV = file.type === 'text/csv';
            if (isCSV) {
                if (this.file_list.some((value) => value.name.toLowerCase() == file.name.toLowerCase())) {
                    this.$notification.error({
                        message: this.$t('validation.exists_already', { attribute: 'file' }),
                    });
                } else {
                    this.file_list = [...this.file_list, file].slice(this.file_limit);
                    this.toggle_vehicle_upload_visibility = true;
                    this.policy_name_list = this.policy_name_list.filter((record) => {
                        return record.is_a_driver == false;
                    });
                }
            } else {
                this.$notification.error({
                    message: this.$t('validation.mimes', { attribute: 'File', values: 'CSV' }),
                });
            }
            return false;
        },
    },
};
</script>
